import { useTranslations } from 'next-intl';

import { Button, TextButton } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import PasskeyIcon from '@flipster/react/icon/generated/PasskeyIcon';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';

export type PasskeyVerifyStep = 'IDLE' | 'VERIFYING' | 'VERIFIED' | 'FAIL';

export interface VerifyWithPasskeyModalProps extends VerifyWithPasskeyContentProps {
  onClose?: () => void;
}
const VerifyWithPasskeyModal = ({ onClose, ...contentProps }: VerifyWithPasskeyModalProps) => {
  const t = useTranslations();
  return (
    <Modal css={{ minH: 538 }}>
      <Modal.Close onClose={() => onClose?.()} />
      <Modal.Header>
        <Modal.Title label={t('Mfa_verify.r250917.Verify_with_passkey')} />
      </Modal.Header>
      <VerifyWithPasskeyContent {...contentProps} />
    </Modal>
  );
};

export default VerifyWithPasskeyModal;

export interface VerifyWithPasskeyContentProps {
  verifyStep: PasskeyVerifyStep;
  onClickTryAgainButton?: () => void;
  onClickUnvailableButton?: () => void;
}

export const VerifyWithPasskeyContent = ({
  verifyStep,
  onClickTryAgainButton,
  onClickUnvailableButton,
}: VerifyWithPasskeyContentProps) => {
  const t = useTranslations();
  const isLoading = ['IDLE', 'VERIFYING'].includes(verifyStep);
  const isFailed = verifyStep === 'FAIL';
  const isVerified = verifyStep === 'VERIFIED';

  return (
    <>
      <Modal.Content>
        <div className={stack({ gap: 0 })}>
          <PasskeyIcon size={80} className={css({ alignSelf: 'center', mb: 24, color: 'content.accent.info' })} />
          {!isFailed && (
            <>
              <p className={css({ mb: 4, textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>
                {t('Mfa_verify.r250917.Complete_verification_passkey_instruction')}
              </p>
              <p className={css({ mb: 32, textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {t('Mfa_verify.r250917.Browser_verification_instruction')}
              </p>
            </>
          )}
          {isFailed && (
            <>
              <p className={css({ mb: 4, textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>
                {t('Common.r250917.Verification_failed')}
              </p>
              <p className={css({ mb: 32, textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {t('Mfa_verify.r250917.Operation_timeout_error_description')}
              </p>
            </>
          )}
          <TextButton
            css={css.raw({ alignSelf: 'flex-start' })}
            onClick={onClickUnvailableButton}
            label={t('Mfa_verify.r250917.Passkeys_not_available')}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          hierarchy='primary'
          size='large'
          isLoading={isLoading}
          withSpinner
          disabled={isVerified}
          onClick={onClickTryAgainButton}
          label={isVerified ? t('Common.r250710.Verified') : t('Common.r250917.Try_again')}
        />
      </Modal.Footer>
    </>
  );
};
